/**
 * Authentication Context
 * Manages authentication state across the application
 * Uses HttpOnly cookies for secure token storage
 *
 * Note: Uses relative paths (/api/...) to leverage Vite proxy in development
 * This avoids cross-origin issues with cookies
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setErrorContext, clearErrorContext } from "@/lib/errorReporter";
import { getEmbedAuthHeaders } from "@/lib/embedSession";
import { apiFetch, apiPost } from "@/lib/api";
import { clearReportCache } from "@/hooks/reportCacheStore";
import { EXPERIENCE } from "@touchworks/shared/platform/experience.js";
import { isSessionReplacedBody, isSessionReplacedEvent, rememberSessionReplaced } from "@/lib/sessionReplaced";
import { NO_LICENSE_REQUIRED, parseOttoLicense } from "@/lib/ottoLicense";

const USER_KEY = "user_data";
const FEATURES_STALE_MS = 60_000; // 60s — skip refetch if data is fresh

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [features, setFeatures] = useState({});
  const [isTrial, setIsTrial] = useState(false);
  const [maxRoleLevel, setMaxRoleLevel] = useState(0);
  const [billingStatus, setBillingStatus] = useState("ok");
  const [billingPaymentLink, setBillingPaymentLink] = useState(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [planName, setPlanName] = useState(null);
  const [planSlug, setPlanSlug] = useState(null);
  const [planExpiresAt, setPlanExpiresAt] = useState(null);
  const [trialOnlyFeatures, setTrialOnlyFeatures] = useState([]);
  // PCA-14: a conta é parceira? Vem do MESMO payload de features (nenhuma
  // requisição nova) e é um irmão de `trial`/`billingStatus`, nunca uma chave
  // dentro do mapa `features` — injetar uma chave sintética ali mentiria sobre
  // o plano para todo o resto do app (billing, limites, trialOnlyFeatures).
  // Começa `false`: antes do primeiro fetch bem-sucedido ninguém é parceiro.
  const [isPartnerAccount, setIsPartnerAccount] = useState(false);
  // Otto: qual "casca" a conta vê (TDD §5). Vem do plano (`Plan.product`) no mesmo payload, irmão de `features`, e nunca
  // das chaves liberadas (em trial todas valem). Falha segura: só o valor exato "otto" liga o modo, porque esconder a
  // Inbox de quem a tem é o pior erro. `isLoading` só cai depois do primeiro fetch, então o redirect pós-login já sabe.
  const [experience, setExperience] = useState(EXPERIENCE.PLATFORM);
  // Otto em conta por licença: esta pessoa tem a licença? Mesmo payload; falha segura = não exigida (lib/ottoLicense.js).
  const [ottoLicense, setOttoLicense] = useState(NO_LICENSE_REQUIRED);
  const featuresLastFetchRef = useRef(0);

  /**
   * Internal helper: fetch features/billing data from API.
   * Updates all feature-related state. Respects signal cancellation.
   * Wrapped in useCallback for reference stability (consumed by SocketContext).
   * Resolves with the account's `experience` when the payload was applied (the login needs it to pick the destination).
   */
  const _fetchFeatures = useCallback(async (signal) => {
    try {
      const featuresRes = await fetch("/api/plans/account/features", {
        credentials: "include",
        headers: getEmbedAuthHeaders(),
      });
      if (featuresRes.ok) {
        const featuresData = await featuresRes.json();
        if (signal?.cancelled) return;
        featuresLastFetchRef.current = Date.now();
        setFeatures(featuresData.features || {});
        setIsTrial(!!featuresData.trial);
        setIsTrialExpired(!!featuresData.trialExpired);
        setBillingStatus(featuresData.billingStatus || "ok");
        setBillingPaymentLink(featuresData.billingPaymentLink || null);
        setPlanName(featuresData.planName || null);
        setPlanSlug(featuresData.planSlug || null);
        setPlanExpiresAt(featuresData.planExpiresAt || null);
        setTrialOnlyFeatures(featuresData.trialOnlyFeatures || []);
        setIsPartnerAccount(!!featuresData.isPartnerAccount);
        const nextExperience = featuresData.experience === EXPERIENCE.OTTO ? EXPERIENCE.OTTO : EXPERIENCE.PLATFORM;
        setExperience(nextExperience);
        setOttoLicense(parseOttoLicense(featuresData.ottoLicense));
        return nextExperience;
      }
    } catch (e) {
      console.error("Failed to fetch features:", e);
    }
  }, []);

  /**
   * Force-refresh features from the API, ignoring staleness.
   * Use after plan changes, billing updates, account switch, etc.
   * Wrapped in useCallback for reference stability (consumed by SocketContext).
   */
  const refreshFeatures = useCallback(async () => {
    await _fetchFeatures();
  }, [_fetchFeatures]);

  const checkAuth = async (signal) => {
    try {
      // EMB-08/EMB-09: no shell embutido, getEmbedAuthHeaders() acrescenta o
      // bearer + o marcador de embed (mesmo par que apiFetch já injeta,
      // lib/api.js) — esta chamada usa fetch() cru (não apiFetch), então
      // precisa do mesmo tratamento manualmente. Sem token, retorna `{}` e
      // este bloco não muda nada (app normal, só o cookie de sempre).
      const response = await fetch("/api/auth/me", {
        credentials: "include",
        headers: getEmbedAuthHeaders(),
      });
      console.log("[Auth] Check Auth Status:", response.status);

      if (signal?.cancelled) return;

      // 429 — rate limited: manter estado ATUAL (não inflar com localStorage)
      if (response.status === 429) {
        console.warn("[Auth] Rate limited (429), mantendo estado atual (sem alterar)");
        return;
      }

      if (response.ok) {
        const userData = await response.json();
        if (signal?.cancelled) return;
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));

        // Set error reporter context for frontend error tracking
        setErrorContext({ userId: userData.id, accountId: userData.accountId });

        if (userData.permissions) setPermissions(userData.permissions);
        if (userData.roles) setRoles(userData.roles);
        if (userData.maxRoleLevel !== undefined)
          setMaxRoleLevel(userData.maxRoleLevel);

        if (userData.accountId) {
          // Only fetch features if data is stale (older than FEATURES_STALE_MS)
          const isFresh = (Date.now() - featuresLastFetchRef.current) < FEATURES_STALE_MS;
          if (!isFresh) {
            await _fetchFeatures(signal);
          }
        }
      } else {
        if (signal?.cancelled) return;
        // Abriu a página com uma sessão que outro login já substituiu (conta por licença): o login explica o motivo.
        if (response.status === 401 && isSessionReplacedBody(await response.json().catch(() => null))) {
          rememberSessionReplaced();
        }
        localStorage.removeItem(USER_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setPermissions([]);
        setRoles([]);
        setFeatures({});
        setIsTrial(false);
        setIsTrialExpired(false);
        setMaxRoleLevel(0);
        setBillingStatus("ok");
        setBillingPaymentLink(null);
        setPlanName(null);
        setPlanSlug(null);
        setPlanExpiresAt(null);
        setTrialOnlyFeatures([]);
        setIsPartnerAccount(false);
        setExperience(EXPERIENCE.PLATFORM);
        setOttoLicense(NO_LICENSE_REQUIRED);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      if (signal?.cancelled) return;
      const cachedUser = localStorage.getItem(USER_KEY);
      if (cachedUser) {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        setIsAuthenticated(true);
        // Restore error reporter context from cache
        setErrorContext({ userId: userData.id, accountId: userData.accountId });
      } else {
        setIsAuthenticated(false);
      }
    } finally {
      if (!signal?.cancelled) setIsLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const signal = { cancelled: false };

    // Skip auth check on public pages when no cached session exists
    // Prevents unnecessary 401 on login/verify/forgot-password pages
    // '/video/' = página pública do convidado da chamada de vídeo (sem sessão).
    const publicPaths = ['/login', '/verify', '/forgot-password', '/reset-password', '/video/'];
    const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p));
    const hasCachedUser = !!localStorage.getItem(USER_KEY);

    if (isPublicPage && !hasCachedUser) {
      setIsLoading(false);
      setIsAuthenticated(false);
      return;
    }

    checkAuth(signal);
    return () => {
      signal.cancelled = true;
    };
  }, []);

  // Listen for auth:expired events from api.js (401 responses)
  // Uses SPA state reset instead of hard page reload to avoid infinite remount loops
  const revokeSession = useCallback((event) => {
    console.warn("[Auth] Session expired (auth:expired event)");
    if (isSessionReplacedEvent(event)) rememberSessionReplaced();
    localStorage.removeItem(USER_KEY);
    setIsAuthenticated(false);
    setUser(null);
    setPermissions([]);
    setRoles([]);
    setFeatures({});
    setIsTrial(false);
    setIsTrialExpired(false);
    setMaxRoleLevel(0);
    setBillingStatus("ok");
    setBillingPaymentLink(null);
    setPlanName(null);
    setPlanSlug(null);
    setPlanExpiresAt(null);
    setTrialOnlyFeatures([]);
    setIsPartnerAccount(false);
    setExperience(EXPERIENCE.PLATFORM);
    setOttoLicense(NO_LICENSE_REQUIRED);
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener('auth:expired', revokeSession);
    return () => window.removeEventListener('auth:expired', revokeSession);
  }, [revokeSession]);

  // Listen for billing:blocked events from api.js (402 responses)
  // Updates billingStatus immediately so BlockedAccountOverlay shows without waiting for poll
  useEffect(() => {
    const handleBillingBlocked = (e) => {
      const data = e.detail || {};
      if (data.billingStatus) {
        setBillingStatus(data.billingStatus);
      } else if (billingStatus === 'ok') {
        // If no explicit status in response, mark as suspended
        setBillingStatus('suspended');
      }
      // Refresh features to get accurate billing data
      _fetchFeatures();
    };
    window.addEventListener('billing:blocked', handleBillingBlocked);
    return () => window.removeEventListener('billing:blocked', handleBillingBlocked);
  }, [_fetchFeatures, billingStatus]);

  const login = async (username, password) => {
    let response;
    try {
      response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Important: receive and store cookies
        body: JSON.stringify({ username, password }),
      });
    } catch {
      throw new Error("Não foi possível efetuar o login no momento. Tente novamente em instantes.");
    }

    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error("Não foi possível efetuar o login no momento. Tente novamente em instantes.");
    }

    if (!response.ok) {
      throw new Error(data?.error || "Falha ao entrar.");
    }

    // Token is now in HttpOnly cookie, just fetch user data
    const meResponse = await fetch("/api/auth/me", {
      credentials: "include",
    });

    if (meResponse.ok) {
      const userData = await meResponse.json();

      // Features ANTES de marcar a sessão: o destino pós-login depende do `experience` (conta Otto cai nas chamadas) e o
      // redirect do Login dispara assim que `user` existe. Sem esperar, a conta Otto aterrissaria na casca da plataforma.
      // Nunca rejeita: se o payload falhar, a experiência fica `platform` (falha segura).
      const loginExperience = userData.accountId ? await _fetchFeatures() : undefined;

      setUser(userData);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      setIsAuthenticated(true);

      // Set error reporter context for frontend error tracking
      setErrorContext({ userId: userData.id, accountId: userData.accountId });

      // Populate RBAC state immediately so sidebar/routes work without refresh
      if (userData.permissions) setPermissions(userData.permissions);
      if (userData.roles) setRoles(userData.roles);
      if (userData.maxRoleLevel !== undefined)
        setMaxRoleLevel(userData.maxRoleLevel);

      // User data plus the experience, for the role-based redirect (the experience is not part of the stored user)
      return { ...userData, experience: loginExperience ?? EXPERIENCE.PLATFORM };
    }

    setIsAuthenticated(true);
    return null;
  };

  const logout = async () => {
    try {
      // Call server to clear HttpOnly cookie
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }

    // Clear local state
    localStorage.removeItem(USER_KEY);
    clearReportCache(); // mesmo motivo do switchAccount: cache fora do React Query
    clearErrorContext(); // Clear error reporter context
    setUser(null);
    setIsAuthenticated(false);
    setPermissions([]);
    setRoles([]);
    setFeatures({});
    setIsTrial(false);
    setIsTrialExpired(false);
    setMaxRoleLevel(0);
    setBillingStatus("ok");
    setBillingPaymentLink(null);
    setPlanName(null);
    setPlanSlug(null);
    setPlanExpiresAt(null);
    setTrialOnlyFeatures([]);
    setIsPartnerAccount(false);
    setExperience(EXPERIENCE.PLATFORM);
    setOttoLicense(NO_LICENSE_REQUIRED);
  };

  // Permission check helpers
  const hasPermission = (permission) => {
    if (maxRoleLevel >= 100) return true; // superadmin has all permissions
    return permissions.includes(permission);
  };

  const hasAnyPermission = (perms) => {
    if (maxRoleLevel >= 100) return true;
    return perms.some((p) => permissions.includes(p));
  };

  const hasAllPermissions = (perms) => {
    if (maxRoleLevel >= 100) return true;
    return perms.every((p) => permissions.includes(p));
  };

  const hasRole = (roleSlug) => {
    return roles.some((r) => r.slug === roleSlug);
  };

  const hasMinLevel = (level) => {
    return maxRoleLevel >= level;
  };

  // Usa o campo isSuperAdmin do usuário (boolean simples)
  const isSuperAdmin = () => user?.isSuperAdmin === true;
  const isAdmin = () => maxRoleLevel >= 50;
  const isSupervisor = () => maxRoleLevel >= 30;

  // Feature check helpers
  // A chave está ligada no PLANO da conta? Não conta o trial (que libera tudo): é para o que não se testa de graça pelo
  // trial, como o vídeo do Otto (quem quer testar usa o Otto Gratuito, incluído por padrão no plano).
  const hasPlanFeature = (featureKey) => {
    const feature = features[featureKey];
    if (!feature) return false;
    if (feature.limitType === "boolean")
      return feature.limitValue === true || feature.limitValue === 1;
    if (feature.limitType === "unlimited") return true;
    if (feature.limitType === "number") return feature.limitValue > 0;
    return false;
  };

  const hasFeature = (featureKey) => {
    if (isTrial) return true; // Trial ativo = tudo liberado
    return hasPlanFeature(featureKey);
  };

  /**
   * Compute trial days remaining from planExpiresAt.
   * Returns 0 if not in trial or no expiry date.
   */
  const trialDaysRemaining = (() => {
    if (!isTrial || !planExpiresAt) return 0;
    const diff = new Date(planExpiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86_400_000));
  })();

  /**
   * Checks if a feature is available ONLY because of the active trial.
   * Returns true if the plan doesn't include this feature (only trial gives access).
   */
  const isTrialOnlyFeature = (featureKey) => {
    if (!isTrial) return false;
    return trialOnlyFeatures.includes(featureKey);
  };

  const getFeatureLimit = (featureKey) => {
    if (isTrial) return Infinity;
    const feature = features[featureKey];
    if (!feature) return 0;
    if (feature.limitType === "unlimited") return Infinity;
    return feature.limitValue || 0;
  };

  const getFeatureUsage = (featureKey) => {
    const feature = features[featureKey];
    return feature?.used || 0;
  };

  const canUseFeature = (featureKey) => {
    if (isTrial) return true; // Trial ativo = tudo liberado
    const feature = features[featureKey];
    if (!feature) return false;
    if (feature.limitType === "boolean")
      return feature.limitValue === true || feature.limitValue === 1;
    if (feature.limitType === "unlimited") return true;
    if (feature.limitType === "number") {
      return (
        (feature.remaining ?? feature.limitValue - (feature.used || 0)) > 0
      );
    }
    return false;
  };

  // Switch account (available for all users with multi-account access)
  const switchAccount = async (accountId) => {
    if (!accountId) throw new Error("accountId é obrigatório");

    // If switching to 'global' (own account), use originalAccountId or current
    const targetAccountId =
      accountId === "global"
        ? user?.originalAccountId || user?.accountId
        : accountId;

    if (!targetAccountId) throw new Error("Conta alvo não encontrada");

    const response = await apiPost("/api/auth/switch-account", {
      accountId: targetAccountId,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || "Falha ao trocar de conta");
    }

    // Clear all React Query cache to prevent multi-tenant data leaking
    // between accounts when switching. The checkAuth() below will trigger
    // a new accountId in context, causing hooks to refetch with fresh keys.
    queryClient.clear();

    // Os relatórios NÃO usam React Query: useReportData tem um Map próprio,
    // com a URL como chave. Como o accountId vem do cookie e não da URL, a
    // chave é idêntica entre contas — sem isto, o relatório da conta anterior
    // seria servido para a conta nova pelos 5 min de TTL.
    clearReportCache();

    // Force features refetch — different account = different plan/features
    featuresLastFetchRef.current = 0;

    // Refresh user data with new account context
    await checkAuth();
  };

  // For backward compatibility - returns empty since token is HttpOnly now
  const getToken = () => {
    console.warn(
      'getToken() is deprecated with HttpOnly cookies. Use credentials: "include" in fetch.',
    );
    return null;
  };

  /**
   * Update user presence status (online/away)
   * Calls API and updates local user state
   */
  const updatePresenceStatus = async (presenceStatus) => {
    try {
      const response = await apiFetch("/api/users/me/presence", {
        method: "PATCH",
        body: JSON.stringify({ presenceStatus }),
      });
      if (response.ok) {
        setUser((prev) => (prev ? { ...prev, presenceStatus } : prev));
        const cached = localStorage.getItem(USER_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          localStorage.setItem(
            USER_KEY,
            JSON.stringify({ ...parsed, presenceStatus }),
          );
        }
      }
    } catch (error) {
      console.error("Failed to update presence:", error);
    }
  };

  /**
   * Persist onboarding/tutorial completion on the user's server profile.
   * Server is the source of truth (survives browser-data clears); the local
   * `user` state + USER_KEY cache are updated optimistically so the current
   * session reflects the change even if the PATCH is in flight or fails.
   *
   * @param {{inboxTutorialDone?: boolean, flowTutorialDone?: boolean, tutorialsDone?: string[]}} patch
   */
  const markOnboarding = async (patch) => {
    if (!patch || typeof patch !== "object") return;

    // Optimistic: merge into the in-memory user and the cached copy.
    setUser((prev) =>
      prev
        ? { ...prev, onboardingProgress: { ...prev.onboardingProgress, ...patch } }
        : prev,
    );
    try {
      const cached = localStorage.getItem(USER_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        localStorage.setItem(
          USER_KEY,
          JSON.stringify({
            ...parsed,
            onboardingProgress: { ...parsed.onboardingProgress, ...patch },
          }),
        );
      }
    } catch {
      /* cache update is best-effort */
    }

    // Durable: persist server-side (best-effort — the optimistic state already
    // suppresses the tutorial for this session if the request fails).
    const userId = user?.userId || user?.localUserId;
    if (!userId) return;
    try {
      await fetch(`/api/users/${userId}/onboarding-progress`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(patch),
      });
    } catch (error) {
      console.error("Failed to persist onboarding progress:", error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getToken,
    checkAuth,
    refreshFeatures,
    switchAccount,
    updatePresenceStatus,
    markOnboarding,

    // RBAC
    permissions,
    roles,
    maxRoleLevel,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasMinLevel,
    isSuperAdmin,
    isAdmin,
    isSupervisor,

    // Features
    features,
    isTrial,
    isTrialExpired,
    hasFeature,
    hasPlanFeature,
    getFeatureLimit,
    getFeatureUsage,
    canUseFeature,
    planName,
    planSlug,
    planExpiresAt,
    trialDaysRemaining,
    trialOnlyFeatures,
    isTrialOnlyFeature,
    isPartnerAccount,
    experience,
    ottoLicense,

    // Billing
    billingStatus,
    billingPaymentLink,
    setBillingStatus,
    setBillingPaymentLink,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
